import { useState } from "react";
import { CheckCircle2, XCircle, Send, RotateCcw } from "lucide-react";

export type QuizQuestion =
  | {
      id: string;
      type: "multiple_choice";
      question: string;
      options: { key: string; text: string }[];
      correct: string;
      rationale: string;
    }
  | {
      id: string;
      type: "reflection";
      question: string;
    };

interface QuizProps {
  questions: QuizQuestion[];
  title?: string;
  onSubmit?: (responses: {
    multipleChoice: Record<string, string>;
    reflection: Record<string, string>;
  }) => void;
}

export function Quiz({ questions, title, onSubmit }: QuizProps) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [reflections, setReflections] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const mcQuestions = questions.filter((q): q is Extract<QuizQuestion, { type: "multiple_choice" }> => q.type === "multiple_choice");
  const reflectionQuestions = questions.filter((q): q is Extract<QuizQuestion, { type: "reflection" }> => q.type === "reflection");

  const allMCAnswered = mcQuestions.every((q) => answers[q.id]);
  const correctCount = mcQuestions.filter((q) => answers[q.id] === q.correct).length;

  const handleSubmit = () => {
    if (!allMCAnswered) return;
    setSubmitted(true);
    onSubmit?.({
      multipleChoice: answers,
      reflection: reflections,
    });
  };

  const handleReset = () => {
    setAnswers({});
    setReflections({});
    setSubmitted(false);
  };

  if (questions.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
        <p className="font-heading font-semibold">Quick Quiz</p>
        <p className="text-sm mt-1">No quiz questions available for this module yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          {title || "Quick Quiz"}
        </h3>
        {submitted && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {correctCount} of {mcQuestions.length} correct
            </span>
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-[var(--color-acrrm-dark)] hover:underline text-sm font-medium"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Multiple choice questions */}
      {mcQuestions.map((q, qIdx) => {
        const selected = answers[q.id];
        const isCorrect = selected === q.correct;

        return (
          <div key={q.id} className="rounded-xl border bg-card p-5">
            <p className="font-medium text-foreground mb-3">
              <span className="text-muted-foreground mr-2">{qIdx + 1}.</span>
              {q.question}
            </p>
            <div className="space-y-2">
              {q.options.map((opt) => {
                const isSelected = selected === opt.key;
                const isCorrectOption = opt.key === q.correct;

                let optionClass = "rounded-lg border p-3 text-sm cursor-pointer transition-colors text-left w-full flex items-start gap-3";

                if (submitted) {
                  if (isCorrectOption) {
                    optionClass += " border-green-300 bg-green-50 text-green-900";
                  } else if (isSelected && !isCorrect) {
                    optionClass += " border-red-300 bg-red-50 text-red-900";
                  } else {
                    optionClass += " border-border bg-muted/30 text-muted-foreground";
                  }
                } else if (isSelected) {
                  optionClass += " border-[var(--color-acrrm-dark)] bg-[var(--color-acrrm-dark)]/5 text-foreground";
                } else {
                  optionClass += " border-border bg-background text-foreground hover:border-[var(--color-acrrm-dark)]/30 hover:bg-muted/30";
                }

                return (
                  <button
                    key={opt.key}
                    onClick={() => {
                      if (submitted) return;
                      setAnswers((a) => ({ ...a, [q.id]: opt.key }));
                    }}
                    disabled={submitted}
                    className={optionClass}
                  >
                    <span className="font-medium text-muted-foreground w-6 flex-shrink-0">{opt.key}.</span>
                    <span className="flex-1">{opt.text}</span>
                    {submitted && isCorrectOption && <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />}
                    {submitted && isSelected && !isCorrect && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className={`mt-3 rounded-lg p-3 text-sm ${isCorrect ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"}`}>
                <p className="font-medium mb-1">{isCorrect ? "Correct!" : `The answer is ${q.correct}.`}</p>
                <p>{q.rationale}</p>
              </div>
            )}
          </div>
        );
      })}

      {/* Reflection questions */}
      {reflectionQuestions.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-heading text-base font-semibold text-foreground">
            Reflection
          </h4>
          {reflectionQuestions.map((q) => (
            <div key={q.id} className="rounded-xl border bg-card p-5">
              <p className="font-medium text-foreground mb-3">{q.question}</p>
              <textarea
                value={reflections[q.id] || ""}
                onChange={(e) => setReflections((r) => ({ ...r, [q.id]: e.target.value }))}
                placeholder="Share your thoughts..."
                rows={4}
                className="w-full rounded-lg border bg-background p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-acrrm-dark)]/20 focus:border-[var(--color-acrrm-dark)]"
              />
            </div>
          ))}
        </div>
      )}

      {/* Submit button */}
      {!submitted && (
        <button
          onClick={handleSubmit}
          disabled={!allMCAnswered}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--color-acrrm-dark)] text-white text-sm font-medium py-2.5 px-6 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="h-4 w-4" />
          Check answers
        </button>
      )}
    </div>
  );
}
